import type { VoidComponent } from "solid-js";
import { createEffect, createSignal, getOwner, runWithOwner } from "solid-js";
import { TextButton } from "../../../components/button/Button";
import FormVersionService from "../../../services/FormVersionService";
import pMinDelay from "p-min-delay";
import { WarningModal } from "../../../components/modal/Modal";
import { useTranslation } from "../../../state/i18n";
import posthog from "posthog-js";
import { MoreAppError } from "../../../MoreAppError";
import TextLink from "../../../components/link/TextLink";
import SaveNotAllowedModal from "./SaveNotAllowedModal";
import { POSTHOG_EVENTS } from "../../../services/PostHogService";
import { useModalManager } from "../../../components/modal/ModalManager";
import { useQueryClient } from "@tanstack/solid-query";
import { useFormState } from "../../../state/form.tsx";

const mailtoHref = import.meta.env.VITE_SUPPORT_MAILTO;

type Status = "draft" | "saving" | "save_not_allowed" | "saved" | "api_error";

const MINIMUM_DELAY = 350;

const HeaderSaveButton: VoidComponent = () => {
  const owner = getOwner();
  const { t } = useTranslation();
  const { open } = useModalManager();
  const form = useFormState();
  const [status, setStatus] = createSignal<Status>("draft");
  const [errorDetails, setErrorDetails] = createSignal<MoreAppError>();
  const queryClient = useQueryClient();
  createEffect(() => {
    if (FormVersionService.hasUpdated() && status() === "saved") {
      setStatus("draft");
    }
  });

  const save = async () => {
    setStatus("saving");
    try {
      await pMinDelay(FormVersionService.save(queryClient), MINIMUM_DELAY, { delayRejection: false });
      setStatus("saved");
      posthog.capture(POSTHOG_EVENTS.DRAFT_SAVED);

      runWithOwner(owner, () => {
        queryClient.invalidateQueries({ queryKey: ["folders"] });
        queryClient.invalidateQueries({ queryKey: ["forms", form.id.toString(), "formVersionsInfinite"] });
      });
    } catch (e) {
      if (e instanceof MoreAppError) {
        setErrorDetails(e);
        if (e.code === "forms_change_not_allowed") {
          setStatus("save_not_allowed");
          return;
        }
      }
      setStatus("api_error");
    }
  };

  createEffect(() => {
    if (status() === "save_not_allowed") {
      open({
        content: () => (
          <SaveNotAllowedModal
            title={t(($) => $.FORM_EDITOR_SAVE_NOT_ALLOWED_TITLE)}
            description={t(($) => $.FORM_EDITOR_SAVE_NOT_ALLOWED_DESCRIPTION)}
          />
        ),
        onClose: () => {
          setErrorDetails(undefined);
          setStatus("draft");
        },
      });
    } else if (status() === "api_error") {
      open({
        content: () => (
          <WarningModal
            confirmLabel={t(($) => $.FORM_EDITOR_SAVE_RETRY_BUTTON)}
            cancelLabel={t(($) => $.CANCEL_BUTTON)}
            title={t(($) => $.FORM_EDITOR_SAVE_ERROR_TITLE)}
            description={
              <>
                <span>{t(($) => $.FORM_EDITOR_SAVE_ERROR_DESCRIPTION)}</span>&nbsp;
                <TextLink label={t(($) => $.SUPPORT_GET_IN_TOUCH_LINK)} href={mailtoHref} />
              </>
            }
            inlineMessage={errorDetails()?.traceId}
            onConfirm={() => {
              setErrorDetails(undefined);
              void save();
            }}
          />
        ),
        onClose: () => {
          setErrorDetails(undefined);
          setStatus("draft");
        },
      });
    }
  });

  return (
    <TextButton
      text={
        status() === "saved"
          ? t(($) => $.FORM_EDITOR_HEADER_SAVE_BUTTON_SAVED)
          : t(($) => $.FORM_EDITOR_HEADER_SAVE_BUTTON)
      }
      disabled={!FormVersionService.hasUpdated()}
      loading={status() === "saving"}
      variant="secondary"
      onClick={save}
    />
  );
};

export default HeaderSaveButton;
